import type { CsatFormComponentConfig, NpsFormComponentConfig } from '../../types';

export const selectedButtonStyles = {
  bgcolor: 'var(--cx-brand-button-selected-bg)',
  borderColor: 'var(--cx-brand-button-selected-border)',
  color: 'var(--cx-brand-button-selected-text)',
};

export const defaultButtonStyles = {
  bgcolor: 'var(--cx-brand-button-bg)',
  borderColor: 'var(--cx-brand-button-border)',
  color: 'var(--cx-brand-button-text)',
};

export type RatingComponentConfig = CsatFormComponentConfig | NpsFormComponentConfig;

export interface RatingState {
  feedback: {
    notes: string;
    tags: string[];
  };
  responseId?: string;
  score: number | null;
  sentiment?: 'negative' | 'neutral' | 'positive';
  submitted: boolean;
}

export const getRatingState = (value: unknown): RatingState => {
  if (typeof value === 'number') {
    return { feedback: { notes: '', tags: [] }, score: value, submitted: false };
  }

  if (typeof value === 'object' && value !== null) {
    const maybeRating = value as {
      feedback?: { notes?: unknown; tags?: unknown };
      responseId?: unknown;
      score?: unknown;
      sentiment?: unknown;
      submitted?: unknown;
    };

    return {
      feedback: {
        notes: typeof maybeRating.feedback?.notes === 'string' ? maybeRating.feedback.notes : '',
        tags: Array.isArray(maybeRating.feedback?.tags)
          ? maybeRating.feedback.tags.filter((tag): tag is string => typeof tag === 'string')
          : [],
      },
      responseId: typeof maybeRating.responseId === 'string' ? maybeRating.responseId : undefined,
      score: typeof maybeRating.score === 'number' ? maybeRating.score : null,
      sentiment:
        maybeRating.sentiment === 'negative' ||
        maybeRating.sentiment === 'neutral' ||
        maybeRating.sentiment === 'positive'
          ? maybeRating.sentiment
          : undefined,
      submitted: maybeRating.submitted === true,
    };
  }

  return { feedback: { notes: '', tags: [] }, score: null, submitted: false };
};

export const ratingSentiment = (
  config: RatingComponentConfig,
  score: number
): 'negative' | 'neutral' | 'positive' => {
  if (config.type === 'npsForm') {
    const detractorMax = Math.min(8, Math.max(0, config.detractorMax ?? 6));
    const neutralMax = Math.min(9, Math.max(detractorMax + 1, config.neutralMax ?? 8));
    if (score <= detractorMax) return 'negative';
    if (score <= neutralMax) return 'neutral';
    return 'positive';
  }

  if (score <= 2) return 'negative';
  if (score === 3) return 'neutral';
  return 'positive';
};

export const buildRatingValue = (
  config: RatingComponentConfig,
  ratingState: RatingState,
  score: number,
  submitted: boolean
) => ({
  componentId: config.id,
  feedback: ratingState.feedback,
  responseId: ratingState.responseId ?? `${config.id}-response`,
  score,
  sentiment: ratingSentiment(config, score),
  submitted,
});
